// src/pages/admin/emails/components/RuleForm.tsx
import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../../../lib/supabaseClient";

type TemplateOption = {
  id: string;
  name: string;
  category: string;
  enabled: boolean;
};

type RuleInitial = {
  id: string;
  template_id: string;
  is_active: boolean;
  trigger_type: string;
  condition_json: any;
  audience_sql: string | null;
  not_before_seconds: number;
  priority: number;
};

export default function RuleForm({
  initial,
  onClose,
  onSaved,
}: {
  initial?: RuleInitial;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = Boolean(initial?.id);

  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [templateId, setTemplateId] = useState(initial?.template_id ?? "");
  const [isActive, setIsActive] = useState(initial?.is_active ?? false);
  const [triggerType, setTriggerType] = useState(initial?.trigger_type ?? "event"); // event|cron
  const [conditionRaw, setConditionRaw] = useState(
    JSON.stringify(initial?.condition_json ?? defaultCondition(), null, 2)
  );
  const [audienceSql, setAudienceSql] = useState(initial?.audience_sql ?? "");
  const [delaySec, setDelaySec] = useState(initial?.not_before_seconds ?? 7200);
  const [priority, setPriority] = useState(initial?.priority ?? 50);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const selectedTpl = useMemo(
    () => templates.find((t) => t.id === templateId) ?? null,
    [templates, templateId]
  );

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("email_templates")
        .select("id, name, category, enabled")
        .order("name", { ascending: true });
      setTemplates((data ?? []) as TemplateOption[]);
    })();
  }, []);

  function parseCondition(): any | null {
    try {
      return JSON.parse(conditionRaw);
    } catch (e: any) {
      setErr("Bedingung ist kein gültiges JSON: " + String(e?.message ?? e));
      return null;
    }
  }

  async function save() {
    setSaving(true);
    setErr(null);

    if (!templateId) {
      setErr("Bitte Vorlage auswählen.");
      setSaving(false);
      return;
    }
    const condition_json = parseCondition();
    if (!condition_json) {
      setSaving(false);
      return;
    }

    const payload = {
      template_id: templateId,
      is_active: isActive,
      trigger_type: triggerType,
      condition_json,
      audience_sql: audienceSql?.trim() || null,
      not_before_seconds: Number(delaySec ?? 0),
      priority: Number(priority ?? 100),
    };

    let error;
    if (isEdit) {
      const res = await supabase.from("email_rules").update(payload).eq("id", initial!.id);
      error = res.error ?? null;
    } else {
      const res = await supabase.from("email_rules").insert(payload);
      error = res.error ?? null;
    }

    setSaving(false);
    if (error) {
      setErr(error.message);
      return;
    }
    onSaved();
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end md:items-center justify-center z-50">
      <div className="bg-[#161b27] border border-[#2a3347] w-full md:max-w-3xl max-h-[95vh] rounded-t-2xl md:rounded-2xl shadow-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[#2a3347] flex items-center justify-between">
          <h3 className="font-semibold text-slate-100">
            {isEdit ? "Regel bearbeiten" : "Neue Regel"}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200">✕</button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto max-h-[75vh]">
          {err && (
            <div className="p-3 bg-red-950/20 text-red-400 border border-red-800/30 rounded">
              {err}
            </div>
          )}

          <label className="block">
            <div className="text-sm text-slate-400 mb-1">Vorlage*</div>
            <select
              className="w-full px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-200 rounded outline-none focus:border-sky-400"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
            >
              <option value="">— bitte wählen —</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} {t.enabled ? "" : "(inaktiv)"} • {t.category}
                </option>
              ))}
            </select>
            {selectedTpl && !selectedTpl.enabled && (
              <div className="text-xs text-amber-400 mt-1">
                Hinweis: Die ausgewählte Vorlage ist derzeit inaktiv.
              </div>
            )}
          </label>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="block">
              <div className="text-sm text-slate-400 mb-1">Trigger</div>
              <select
                className="w-full px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-200 rounded outline-none focus:border-sky-400"
                value={triggerType}
                onChange={(e) => setTriggerType(e.target.value)}
              >
                <option value="event">event</option>
                <option value="cron">cron</option>
              </select>
            </label>

            <label className="block">
              <div className="text-sm text-slate-400 mb-1">Delay (Sekunden)</div>
              <input
                type="number"
                className="w-full px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400"
                value={delaySec}
                onChange={(e) => setDelaySec(Number(e.target.value))}
                min={0}
              />
              <div className="text-xs text-slate-500 mt-1">{formatDelay(delaySec)}</div>
            </label>

            <label className="block">
              <div className="text-sm text-slate-400 mb-1">Priorität</div>
              <input
                type="number"
                className="w-full px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400"
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value))}
                min={0}
              />
              <div className="text-xs text-slate-500 mt-1">Niedriger = wichtiger</div>
            </label>
          </div>

          <label className="block">
            <div className="text-sm text-slate-400 mb-1">Bedingung (JSON)</div>
            <textarea
              value={conditionRaw}
              onChange={(e) => setConditionRaw(e.target.value)}
              rows={10}
              className="w-full px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded font-mono outline-none focus:border-sky-400"
              spellCheck={false}
            />
            <div className="text-xs text-slate-500 mt-1">
              Beispiel:
              <pre className="bg-[#0d1117] border border-[#2a3347] p-2 mt-1 rounded whitespace-pre-wrap text-slate-300">
{JSON.stringify(defaultCondition(), null, 2)}
              </pre>
            </div>
          </label>

          <label className="block">
            <div className="text-sm text-slate-400 mb-1">Audience (optional, SQL)</div>
            <textarea
              value={audienceSql ?? ""}
              onChange={(e) => setAudienceSql(e.target.value)}
              rows={4}
              placeholder="Optionaler SQL-Filter (z. B. zusätzliche WHERE-Klauseln auf trips/profiles)"
              className="w-full px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded font-mono outline-none focus:border-sky-400 placeholder:text-slate-600"
            />
          </label>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="accent-sky-500"
              />
              <span className="text-slate-300">Regel aktivieren</span>
            </label>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-[#2a3347] flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded border border-[#2a3347] bg-[#1e2639] text-slate-300 hover:bg-[#2a3347] transition-colors">
            Abbrechen
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-60 transition-colors"
          >
            {saving ? "Speichern…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}

function defaultCondition() {
  return {
    name: "zero_progress_after_creation",
    window_hours: 6,
    requirements: [
      { metric: "program_items", eq: 0 },
      { metric: "documents", eq: 0 },
      { metric: "participants", eq: 0 },
      { metric: "todos", eq: 0 }
    ]
  };
}

function formatDelay(seconds: number) {
  if (!seconds || seconds <= 0) return "ohne Verzögerung";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}
